import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Hover/active are ramp steps per docs/15 §7, not opacity — the semantic colors
 * carry no <alpha-value> slot, so Tailwind `/90`-style modifiers would silently
 * no-op on them. The light accent starts at Green 800, so pressed shares Green
 * 900 with hover and adds a 1px down-shift to stay distinct (docs/15 §7).
 * Destructive has no ramp (status constant), so it dims by element opacity.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active active:translate-y-px',
        // The signature-gradient CTA (docs/15 §2; one per view, ≥90px wide). Navy ink at
        // 9.06:1 on the green stop and 8.57:1 on the cyan — white reaches only 1.98:1 and
        // 2.10:1, so it is not an option here. No ramp exists for a gradient, so hover is a
        // brightness step (which *raises* both ratios) and pressed the standard down-shift.
        // This is the kit's own `.btn-primary`, and it is not what docs/15 §10 deviation 1
        // corrected — that deviation is about the flat `--accent` fill (`variant="default"`).
        //
        // Pill radius (M18): the kit's own `.btn-primary` is `--r-full`, and every green
        // button on the group site is a pill. `rounded-full` overrides the base `rounded-md`
        // because it comes later in the class string — the size variants restate `rounded-md`,
        // so `sm`/`lg` need it back, which is why it is repeated in `compoundVariants` below.
        brand:
          'rounded-full bg-brand text-navy-800 hover:brightness-110 active:translate-y-px',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        outline: 'border border-input bg-surface hover:bg-secondary',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        ghost: 'hover:bg-secondary',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6',
        icon: 'h-9 w-9',
      },
    },
    compoundVariants: [
      // `size` restates `rounded-md`, which would win over the variant's pill; restore it.
      { variant: 'brand', size: 'sm', className: 'rounded-full' },
      { variant: 'brand', size: 'lg', className: 'rounded-full' },
    ],
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
